/**
 * DimmingOverlay Component
 * Feature: 057-lightbox-fullscreen-oneup
 *
 * Provides a semi-transparent black overlay for "Dimmed Lights" mode.
 * Dims UI elements (header, collection panel, comments) while keeping photos at full brightness.
 *
 * Usage:
 * ```tsx
 * <div className="relative">
 *   <header>...</header>
 *   {isDimmedLights && <DimmingOverlay isActive={true} />}
 * </div>
 * ```
 */

import { cn } from '@/lib/utils';

export interface DimmingOverlayProps {
  /**
   * Whether the dimming overlay is currently active
   */
  isActive: boolean;

  /**
   * Opacity of the dimming overlay (0-1)
   * @default 0.75 (75% opacity, meets 70-80% spec requirement)
   */
  opacity?: number;

  /**
   * Z-index for the overlay layer
   * @default 10 (lower than modals which use z-50)
   */
  zIndex?: number;

  /**
   * Optional additional CSS classes
   */
  className?: string;
}

/**
 * DimmingOverlay renders a semi-transparent black overlay
 * when dimmed lights mode is active.
 *
 * Implementation details:
 * - Uses absolute positioning to cover entire parent element
 * - pointer-events: none allows interaction with dimmed UI beneath
 * - GPU-accelerated rendering via opacity transitions (< 1ms render time)
 * - Smooth fade in/out via CSS transitions (200ms)
 * - Always in DOM to enable smooth transitions
 * - aria-hidden hides from screen readers (presentational only)
 *
 * Performance: <1ms render time (GPU-accelerated opacity)
 */
export function DimmingOverlay({
  isActive,
  opacity = 0.75,
  zIndex = 10,
  className,
}: DimmingOverlayProps) {
  return (
    <div
      className={cn(
        // Absolute positioning - covers parent container only
        'absolute inset-0',
        // Black background with opacity
        'bg-black',
        // Pointer events disabled - allow interaction with dimmed UI
        'pointer-events-none',
        // Smooth fade transition (200ms per spec)
        'transition-opacity duration-200 ease-in-out',
        // Custom classes
        className
      )}
      style={{
        // Fade between 0 (transparent) and target opacity (default 0.75)
        opacity: isActive ? opacity : 0,
        zIndex: zIndex,
      }}
      aria-hidden="true"
      data-testid="dimming-overlay"
    />
  );
}

export default DimmingOverlay;
